import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ReactSVG from 'react-svg'
import { useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { map } from 'ramda'
import { useQuery } from '@apollo/react-hooks'

import NavigationItem from './navigation-item'
import NavigationRight from './navigation-right'
import NavigationBurgerIcon from './navigation-burger-icon'
import kidsLogo from '../../../images/neon/kids-logo.svg'
import styles from './navigation-header.css'
import NAV_HEADER_QUERY from '../../../graphql/queries/navigation-header.gql'
import useSearchParams from '../hooks/useSearchParams'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { useIsKid } from '../../hooks/use-is-kid'
import {
  getMainNavHeaderSectionLinks,
  addKidsLinkForNonKidProfiles
} from './utils/navigation-links-helpers'
import { navigationBarGa } from '../../lib/analytics/ga'
import { customDimensions } from '../../lib/analytics/custom-dimensions'
import { getSelectedProfileIdFromSession } from '../../lib/account'
import { UseNotFoundContext } from '../../components/not-found/hooks/use-not-found-context'
import { E2E_SELECTORS } from '../../e2e-selectors'

const NavigationHeader = ({ isMyAccountLayout }) => {
  const { data } = useQuery(NAV_HEADER_QUERY, { fetchPolicy: 'cache-only' })
  const isAuthenticated = useIsAuthenticated()
  const isKid = useIsKid()
  const sections = data?.navHeader

  const profileId = useSelector(state => getSelectedProfileIdFromSession(state.session))
  const { hasSearchParams } = useSearchParams()
  const currentLocation = useLocation()
  const [{ isNotFoundRendered }] = useContext(UseNotFoundContext)

  // renderNothing if no navHeader
  if (!sections) return null

  const mainSectionChildren = getMainNavHeaderSectionLinks({ navHeader: sections })
  const mainSectionLinks = addKidsLinkForNonKidProfiles(isAuthenticated, isKid, mainSectionChildren)

  /**
   * Check if current path matches the navItem or its sub-nav item
   * Active link in two scenarios:
   * current path match nav item path, e.g. /tv === /tv
   * current path match one of the sub nav item path, e.g. /browse-movies/comedy === /browse-movies/comedy
   */
  const isActiveLink = (navItem) => {
    return (currentLocation.pathname === navItem.path) ||
      navItem.children.some(item => item.path === currentLocation.pathname)
  }

  // Check if the user is authenticated and a kid for showing the kids logo
  const renderKidsLogo = (isAuthenticated && isKid)

  return (
    <nav
      data-e2e="nav nav-header"
      className={classNames(styles.nav, {
        [styles.fixedNav]: (isMyAccountLayout && !hasSearchParams) || isNotFoundRendered
      })}
    >
      <NavigationBurgerIcon display={false} />
      <NavLink
        exact
        to="/"
        data-e2e={isAuthenticated ? 'nav-logged-in-logo' : 'nav-guest-logo'}
        onClick={() => {
          navigationBarGa({ action: 'Logo', [customDimensions.ProfileId]: profileId })
        }}
        className={classNames(styles.navLink, styles.navLinkLogo)}
      />
      {renderKidsLogo && (
        <NavLink
          to="/"
          className={styles.kidsLogoLink}
          onClick={() => {
            navigationBarGa({ action: 'Logo', [customDimensions.ProfileId]: profileId })
          }}
        >
          <ReactSVG
            src={kidsLogo}
            data-lbx-e2e={E2E_SELECTORS.KIDS_LOGO}
            className={styles.kidsLogo}
          />
        </NavLink>
      )}
      {map(navItem => {
        if (navItem.isDiscoverNav) return null
        const isActive = isKid ? false : isActiveLink(navItem)
        return (
          <NavigationItem
            key={`${navItem.name}${navItem.path}`}
            navItem={navItem}
            className={classNames(styles.navLink, {
              [styles.isRental]: navItem.isRental
            })}
            activeClassName={classNames({ [styles.navLinkActive]: isActive })}
            isInsideNavigationBar
            expandOnHover
          />
        )
      }, mainSectionLinks)}
      <NavigationRight className={styles.navRight} />
    </nav>
  )
}

NavigationHeader.propTypes = {
  isMyAccountLayout: PropTypes.bool
}

NavigationHeader.defaultProps = {
  isMyAccountLayout: false
}

export default NavigationHeader
